import { useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  Button,
  Card,
  CardActions,
  CardMedia,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { postUploadFile } from './etc/index';
import Form from 'components/Form';
import MuiDialog from './components/MuiDialog';
import Uploader from './components/Uploader';
import useNotification from 'hooks/useNotification';

import { IEtcImages } from '../PatientList/api/type';

interface Props {
  isUpdate: boolean;
  defaultValue: Partial<IEtcImages>;
  onClose: () => void;
  onSave: (data: IEtcImages) => void;
  onSearchNo: (no: number) => boolean;
}

const EtcDialog = (props: Props) => {
  const { isUpdate, defaultValue, onClose, onSave, onSearchNo } = props;

  const { onSuccess, onRequired, onResultCode, onFail } = useNotification();
  const { handleSubmit, register, getValues, setValue } = useForm({
    defaultValues: defaultValue,
  });

  const [isSearched, setIsSearched] = useState(isUpdate);

  const [files, setFiles] = useState<string[]>(
    defaultValue.miscellaneousImages ?? []
  );

  const onUploadFile = (file: File) => {
    if (files.length === 5) return onRequired('REQUIRED.RADIOLOGY.MAX.FILE');

    postUploadFile({ file })
      .then(({ data: { rc, bucket_url } }) => {
        if (rc !== 1) return onResultCode(rc);
        setFiles([...files, bucket_url]);
        onSuccess('檔案上傳成功。');
      })
      .catch(e => onFail('檔案上傳失敗。', e));
  };

  const DeleteFile = ({ index }: { index: number }) => {
    const onDelete = () =>
      setFiles(files.filter((_, fIndex) => fIndex !== index));
    return (
      <Button fullWidth size="small" color="error" onClick={onDelete}>
        刪除影像檔{index + 1}
      </Button>
    );
  };

  const onSubmit = (data: any) => {
    onSuccess('其他紀錄資料已成功儲存。');
    onSave({ ...data, miscellaneousImages: files });
    onClose();
  };

  return (
    <MuiDialog.SaveForm
      isOpen
      maxWidth="md"
      title="其他紀錄資料"
      onClose={onClose}
      onSubmit={e => {
        e.stopPropagation();
        handleSubmit(onSubmit)(e);
      }}
    >
      <Grid container gap={2} sx={{ my: 2 }}>
        <Form.Item required label={'編號'}>
          <TextField
            required
            size="small"
            type="number"
            {...register('ptMiscellaneousNo')}
            InputProps={{ readOnly: isUpdate }}
          />
        </Form.Item>
        <Form.Item required label={'記錄名稱（韓）'}>
          <TextField
            required
            size="small"
            {...register('ptMiscellaneousItem')}
          />
        </Form.Item>

        <Grid item xs={12} />

        <Grid item xs={12}>
          <Grid
            container
            alignItems={'center'}
            justifyContent={'space-between'}
          >
            <Stack spacing={1}>
              <Typography variant="body2">請選擇要上傳的檔案</Typography>
              <Typography variant="caption" color="gray">
                最大 5 個、每個 5MB 以下，僅支援 .png、.jpg、.jpeg。
              </Typography>
            </Stack>
            <Uploader
              maxSize={5}
              buttonLabel="上傳檔案"
              id="radiology-uploader"
              accept=".png, .jpg, .jpeg"
              onChange={fileList => onUploadFile(fileList[0])}
            />
          </Grid>
        </Grid>

        {files.map((file, index) => (
          <Grid item xs={2.4} key={file}>
            <Form.Item label={`影像檔${index + 1}`}>
              <Card sx={{ maxWidth: '100%' }} variant="outlined">
                <CardMedia component="img" image={file} />
                <CardActions>
                  <DeleteFile index={index} />
                </CardActions>
              </Card>
            </Form.Item>
          </Grid>
        ))}
      </Grid>
    </MuiDialog.SaveForm>
  );
};

export default EtcDialog;
